import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../../api';
import Modal from '../../components/Modal';

export default function TeamsPositionsPage() {
  const [teams, setTeams] = useState([]);
  const [positions, setPositions] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Active team tab
  const [activeTeamId, setActiveTeamId] = useState(null);
  const [members, setMembers] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(false);

  // Team modal
  const [showTeamModal, setShowTeamModal] = useState(false);
  const [teamForm, setTeamForm] = useState({ name: '', description: '', parent_team_id: null });
  const [editingTeam, setEditingTeam] = useState(null);
  const [savingTeam, setSavingTeam] = useState(false);

  // Move-member state (per-row)
  const [movingUserId, setMovingUserId] = useState(null);

  // Add member modal
  const [showAddModal, setShowAddModal] = useState(false);
  const [addUserId, setAddUserId] = useState('');
  const [addAsManager, setAddAsManager] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const [t, p, u] = await Promise.all([
        api.getTeams(),
        api.getPositions(),
        api.getActiveUsers().catch(() => []),
      ]);
      setTeams(t);
      setPositions(p);
      setAllUsers(Array.isArray(u) ? u : []);
      if (!activeTeamId && t.length > 0) setActiveTeamId(t[0].id);
    } catch (err) {
      console.error('Failed to load:', err);
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line

  useEffect(() => { loadData(); }, [loadData]);

  const loadMembers = useCallback(async (teamId) => {
    if (!teamId) return;
    setLoadingMembers(true);
    try {
      setMembers(await api.getTeamMembers(teamId));
    } catch {
      setMembers([]);
    } finally {
      setLoadingMembers(false);
    }
  }, []);

  useEffect(() => {
    if (activeTeamId) loadMembers(activeTeamId);
  }, [activeTeamId, loadMembers]);

  // ── TEAM CRUD ──
  const handleTeamSubmit = async (e) => {
    e.preventDefault();
    if (!teamForm.name.trim()) return;
    setSavingTeam(true);
    try {
      const payload = {
        name: teamForm.name,
        description: teamForm.description,
        parent_team_id: teamForm.parent_team_id || null,
      };
      if (editingTeam) {
        await api.updateTeam(editingTeam.id, payload);
      } else {
        const newTeam = await api.createTeam(payload);
        setActiveTeamId(newTeam.id);
      }
      closeTeamModal();
      await loadData();
    } catch (err) {
      alert(err.message || 'Failed to save team');
    } finally {
      setSavingTeam(false);
    }
  };

  const closeTeamModal = () => {
    setShowTeamModal(false);
    setEditingTeam(null);
    setTeamForm({ name: '', description: '', parent_team_id: null });
  };

  // Move a member from the active team to a different team (used by the
  // "Move to…" dropdown on each member row). Calls the atomic move endpoint.
  const moveMember = async (userId, newTeamId) => {
    if (!activeTeamId || !newTeamId) return;
    try {
      await api.moveTeamMember(activeTeamId, userId, newTeamId);
      await loadMembers(activeTeamId);
      await loadData();
    } catch (err) {
      alert(err.message || 'Failed to move member');
    } finally {
      setMovingUserId(null);
    }
  };

  // ── MEMBER ACTIONS ──
  const addMember = async () => {
    if (!addUserId || !activeTeamId) return;
    try {
      await api.addTeamMember(activeTeamId, parseInt(addUserId), addAsManager);
      setAddUserId('');
      setAddAsManager(false);
      setShowAddModal(false);
      await loadMembers(activeTeamId);
      await loadData();
    } catch (err) {
      alert(err.message || 'Failed to add member');
    }
  };

  const toggleManager = async (userId, currentIsManager) => {
    try {
      await api.updateTeamMember(activeTeamId, userId, !currentIsManager);
      await loadMembers(activeTeamId);
    } catch (err) {
      alert(err.message || 'Failed to update');
    }
  };

  const removeMember = async (userId, name) => {
    if (!window.confirm(`Remove ${name} from this team?`)) return;
    try {
      await api.removeTeamMember(activeTeamId, userId);
      await loadMembers(activeTeamId);
      await loadData();
    } catch (err) {
      alert(err.message || 'Failed to remove');
    }
  };

  if (loading) return <div className="loading">Loading...</div>;

  const activeTeam = teams.find(t => t.id === activeTeamId);
  const availableUsers = allUsers.filter(u => !members.some(m => m.id === u.id));

  return (
    <div className="admin-page">
      <div className="page-header">
        <h2>Teams & Members</h2>
      </div>

      {/* Team tree tabs — parents first, sub-teams indented under each */}
      {(() => {
        const parentTeams = teams.filter(t => !t.parent_team_id);
        const subTeamsByParent = {};
        teams.filter(t => t.parent_team_id).forEach(t => {
          (subTeamsByParent[t.parent_team_id] = subTeamsByParent[t.parent_team_id] || []).push(t);
        });

        const renderTeamButton = (t, isSub) => {
          const isActive = activeTeamId === t.id;
          return (
            <button
              key={t.id}
              onClick={() => setActiveTeamId(t.id)}
              style={{
                padding: isSub ? '8px 14px 8px 28px' : '10px 20px',
                fontSize: isSub ? 12 : 13,
                fontWeight: isActive ? 700 : 500,
                color: isActive ? '#fff' : 'var(--text-secondary)',
                background: isActive ? 'var(--sidebar-bg, #1e293b)' : 'transparent',
                border: 'none',
                borderLeft: isSub ? '2px solid var(--border-subtle)' : 'none',
                borderRadius: isActive ? '8px 8px 0 0' : 8,
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                transition: 'all 0.15s ease',
                display: 'flex', alignItems: 'center', gap: 8,
              }}
            >
              {isSub && <span style={{ fontSize: 10, color: isActive ? 'rgba(255,255,255,0.5)' : 'var(--text-muted)' }}>↳</span>}
              {t.name}
              <span style={{
                fontSize: 10, fontWeight: 700,
                padding: '1px 7px', borderRadius: 8,
                background: isActive ? 'rgba(255,255,255,0.15)' : 'var(--bg-subtle)',
                color: isActive ? 'rgba(255,255,255,0.7)' : 'var(--text-muted)',
              }}>{t.direct_member_count ?? t.member_count}</span>
            </button>
          );
        };

        return (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
            marginBottom: 0, paddingBottom: 0,
            background: 'var(--bg-card)',
            borderRadius: '12px 12px 0 0',
            padding: '6px 6px 0',
            border: '1px solid var(--border-subtle)',
            borderBottom: 'none',
          }}>
            {parentTeams.map(parent => (
              <React.Fragment key={parent.id}>
                {renderTeamButton(parent, false)}
                {(subTeamsByParent[parent.id] || []).map(sub => renderTeamButton(sub, true))}
              </React.Fragment>
            ))}
            <button
              onClick={() => { setEditingTeam(null); setTeamForm({ name: '', description: '', parent_team_id: null }); setShowTeamModal(true); }}
              style={{
                padding: '8px 14px', fontSize: 12, fontWeight: 600,
                color: 'var(--brand-gold)', background: 'rgba(217, 119, 6, 0.08)',
                border: '1px dashed var(--brand-gold)', borderRadius: 8,
                cursor: 'pointer', marginLeft: 'auto', whiteSpace: 'nowrap',
              }}
            >+ New Team</button>
          </div>
        );
      })()}

      {/* Team header bar */}
      {activeTeam && (
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '16px 18px',
          background: 'var(--bg-card)',
          borderLeft: '1px solid var(--border-subtle)',
          borderRight: '1px solid var(--border-subtle)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>
              {activeTeam.name}
            </h3>
            {activeTeam.description && (
              <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{activeTeam.description}</span>
            )}
            <button
              className="btn btn-muted btn-sm"
              style={{ fontSize: 12, padding: '5px 12px' }}
              onClick={() => { setEditingTeam(activeTeam); setTeamForm({ name: activeTeam.name, description: activeTeam.description || '', parent_team_id: activeTeam.parent_team_id || null }); setShowTeamModal(true); }}
            >Edit</button>
          </div>
          <button className="btn btn-gold btn-sm" onClick={() => { setAddUserId(''); setAddAsManager(false); setShowAddModal(true); }}>
            + Add Member
          </button>
        </div>
      )}

      {/* Members table */}
      {activeTeam && (
        <div className="table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th style={{ textAlign: 'left' }}>Name</th>
                <th style={{ textAlign: 'left' }}>Position</th>
                <th style={{ textAlign: 'center', width: 100 }}>Role</th>
                <th style={{ width: 80 }}></th>
              </tr>
            </thead>
            <tbody>
              {loadingMembers && (
                <tr><td colSpan={4} style={{ textAlign: 'center', padding: 24, color: 'var(--text-muted)' }}>Loading...</td></tr>
              )}
              {!loadingMembers && members.length === 0 && (
                <tr><td colSpan={4} style={{ textAlign: 'center', padding: 32, color: 'var(--text-muted)' }}>No members yet. Click "+ Add Member" to get started.</td></tr>
              )}
              {!loadingMembers && members.map(m => (
                <tr key={m.id}>
                  <td style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{m.full_name}</td>
                  <td>{m.position_name || <span style={{ color: 'var(--text-muted)' }}>—</span>}</td>
                  <td style={{ textAlign: 'center' }}>
                    {m.is_manager ? (
                      <span
                        onClick={() => toggleManager(m.id, true)}
                        style={{
                          display: 'inline-block', fontSize: 11, fontWeight: 700,
                          padding: '4px 12px', borderRadius: 10, cursor: 'pointer',
                          background: 'linear-gradient(135deg, rgba(217, 119, 6, 0.12), rgba(217, 119, 6, 0.06))',
                          color: '#b45309', border: '1px solid rgba(217, 119, 6, 0.2)',
                        }}
                      >Manager</span>
                    ) : (
                      <span
                        onClick={() => toggleManager(m.id, false)}
                        style={{
                          display: 'inline-block', fontSize: 11, fontWeight: 500,
                          padding: '4px 12px', borderRadius: 10, cursor: 'pointer',
                          background: 'var(--bg-subtle)', color: 'var(--text-muted)',
                          border: '1px solid var(--border-subtle)',
                        }}
                      >Member</span>
                    )}
                  </td>
                  <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                    {(() => {
                      // Candidates to move to: sibling sub-teams (same parent) + own parent (move UP)
                      // + the parent of OTHER trees is NOT listed (keeps reassignment scoped).
                      const active = teams.find(t => t.id === activeTeamId);
                      if (!active) return null;
                      const siblingIds = active.parent_team_id
                        ? teams.filter(t => t.parent_team_id === active.parent_team_id && t.id !== active.id).map(t => t.id)
                        : [];
                      const parentId = active.parent_team_id;
                      const candidateIds = [...new Set([...siblingIds, parentId].filter(Boolean))];
                      const candidates = candidateIds.map(id => teams.find(t => t.id === id)).filter(Boolean);
                      if (candidates.length === 0) return (
                        <button className="btn btn-muted btn-sm" style={{ fontSize: 11, padding: '4px 10px', color: 'var(--text-muted)' }} onClick={() => removeMember(m.id, m.full_name)}>Remove</button>
                      );
                      return movingUserId === m.id ? (
                        <select
                          autoFocus
                          defaultValue=""
                          onBlur={() => setMovingUserId(null)}
                          onChange={(e) => { if (e.target.value) moveMember(m.id, Number(e.target.value)); }}
                          style={{ fontSize: 12, padding: '4px 8px' }}
                        >
                          <option value="">Pick team…</option>
                          {candidates.map(c => (
                            <option key={c.id} value={c.id}>
                              Move to {c.name}{c.parent_team_id ? '' : ' (parent)'}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <>
                          <button
                            className="btn btn-muted btn-sm"
                            style={{ fontSize: 11, padding: '4px 10px', marginRight: 4 }}
                            onClick={() => setMovingUserId(m.id)}
                          >Move</button>
                          <button
                            className="btn btn-muted btn-sm"
                            style={{ fontSize: 11, padding: '4px 10px', color: 'var(--text-muted)' }}
                            onClick={() => removeMember(m.id, m.full_name)}
                          >Remove</button>
                        </>
                      );
                    })()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add Member Modal */}
      {showAddModal && (
        <Modal title={`Add Member to ${activeTeam?.name}`} onClose={() => setShowAddModal(false)}>
          <div className="form-group">
            <label>User</label>
            <select
              className="form-input"
              value={addUserId}
              onChange={(e) => setAddUserId(e.target.value)}
            >
              <option value="">Select a user...</option>
              {availableUsers.map(u => (
                <option key={u.id} value={u.id}>{u.full_name}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', textTransform: 'none', letterSpacing: 0, fontSize: 14, fontWeight: 500, color: 'var(--text-primary)' }}>
              <input
                type="checkbox"
                checked={addAsManager}
                onChange={(e) => setAddAsManager(e.target.checked)}
                style={{ width: 16, height: 16 }}
              />
              Add as manager of this team
            </label>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
              Managers get elevated permissions in modules this team has access to.
            </div>
          </div>
          <div className="modal-actions">
            <button className="btn btn-muted" onClick={() => setShowAddModal(false)}>Cancel</button>
            <button className="btn btn-primary" onClick={addMember} disabled={!addUserId}>Add Member</button>
          </div>
        </Modal>
      )}

      {/* Team Create/Edit Modal */}
      {showTeamModal && (
        <Modal title={editingTeam ? 'Edit Team' : 'New Team'} onClose={closeTeamModal}>
          <form onSubmit={handleTeamSubmit}>
            <div className="form-group">
              <label>Team Name</label>
              <input
                className="form-input"
                value={teamForm.name}
                onChange={(e) => setTeamForm({ ...teamForm, name: e.target.value })}
                placeholder="e.g. Sales, Dispatch, Dallas Drivers"
                autoFocus
              />
            </div>
            <div className="form-group">
              <label>Description</label>
              <input
                className="form-input"
                value={teamForm.description}
                onChange={(e) => setTeamForm({ ...teamForm, description: e.target.value })}
                placeholder="Brief description (optional)"
              />
            </div>
            <div className="form-group">
              <label>Parent Team <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(optional — leave blank for a top-level team)</span></label>
              <select
                className="form-input"
                value={teamForm.parent_team_id || ''}
                onChange={(e) => setTeamForm({ ...teamForm, parent_team_id: e.target.value ? Number(e.target.value) : null })}
                disabled={!!(editingTeam && (editingTeam.direct_member_count > 0 || editingTeam.children_count > 0))}
              >
                <option value="">— None (top-level team) —</option>
                {teams.filter(t => !t.parent_team_id && (!editingTeam || t.id !== editingTeam.id)).map(t => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
              {editingTeam && (editingTeam.direct_member_count > 0 || editingTeam.children_count > 0) && (
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
                  Reparenting is disabled while this team has members or sub-teams. Move them first.
                </div>
              )}
            </div>
            <div className="modal-actions">
              <button type="button" className="btn btn-muted" onClick={closeTeamModal}>Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={savingTeam || !teamForm.name.trim()}>
                {savingTeam ? 'Saving...' : (editingTeam ? 'Update' : 'Create Team')}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
